/**
 * Grid View Component
 * Displays the 4x4 grid of scale positions
 */

import { useMScaleStore } from '../../store'
import { GridCell } from './GridCell'

interface GridViewProps {
  onHover?: (position: number) => void
}

export function GridView({ onHover }: GridViewProps = {}) {
  const { weights, groups, hardwareConfig, setSelectedPosition } = useMScaleStore()

  // Default to 4x4 grid if no hardware config
  const gridRows = hardwareConfig?.gridRows ?? 4
  const gridCols = hardwareConfig?.gridCols ?? 4
  const activePositions = hardwareConfig?.activePositions ?? Array.from({ length: 16 }, (_, i) => i)

  // Create group lookup map for O(1) access
  const positionToGroupId = new Map<number, number>()
  groups?.forEach((group) => {
    if (group.isValid) {
      // Map all positions in the group to the same group ID
      group.positions.forEach((position) => {
        positionToGroupId.set(position, group.groupId)
      })
    }
  })

  const handleCellClick = (position: number) => {
    setSelectedPosition(position)
  }

  // Generate grid cells (row by row, left to right)
  const cells = []
  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridCols; col++) {
      const position = row * gridCols + col
      const weight = weights[position] ?? 0
      const isActive = activePositions.includes(position)
      const groupId = positionToGroupId.get(position) ?? null

      cells.push(
        <GridCell
          key={position}
          position={position}
          weight={weight}
          groupId={groupId}
          isActive={isActive}
          onClick={() => handleCellClick(position)}
          onHover={onHover ? () => onHover(position) : undefined}
        />
      )
    }
  }

  return (
    <div
      className="grid gap-3 md:gap-4 w-full max-w-4xl mx-auto"
      style={{
        gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${gridRows}, minmax(0, 1fr))`,
      }}
    >
      {cells}
    </div>
  )
}
